/**
 * Retry utility for handling 401 errors with token refresh
 * Waits 5 seconds after sending unauthorized event to allow parent to refresh token
 */

import { CenoraInternalCommunication } from '@cenora-llc/cenora-internal-communication-library'

/**
 * Sleep for specified milliseconds
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Retry delay in milliseconds (5 seconds)
 */
export const RETRY_DELAY_MS = 5000

/**
 * Send unauthorized event to parent app
 */
export function sendUnauthorizedEvent() {
  console.error('[Retry] Unauthorized error (401), sending basic-unauthorized-event')
  CenoraInternalCommunication.eventSender.sendMessage(
    'error',
    'basic-unauthorized-event',
    true
  )
}

/**
 * Retry an async function once after 5 seconds if it throws a 401 error
 * Sends unauthorized event to parent and waits for token refresh
 *
 * @param {Function} fn - Async function to retry
 * @param {string} context - Context name for logging (e.g., 'Axios', 'API', 'Tracking')
 * @returns {Promise<any>} - Result from the function
 */
export async function retryWith401Detection(fn, context = 'Request') {
  try {
    return await fn()
  } catch (error) {
    // Check if it's a 401 error
    const is401 = error.response?.status === 401 || error.status === 401

    if (is401) {
      console.warn(`[${context}] First attempt failed with 401, notifying parent and retrying in ${RETRY_DELAY_MS}ms...`)

      // Send unauthorized event to parent
      sendUnauthorizedEvent()

      // Wait 5 seconds for parent to refresh token in cookie
      await sleep(RETRY_DELAY_MS)

      // Retry once with fresh token from cookie
      console.log(`[${context}] Retrying request after token refresh delay...`)
      try {
        return await fn()
      } catch (retryError) {
        console.error(`[${context}] Retry failed:`, retryError.message)
        throw retryError
      }
    }

    // Not a 401 error, just throw it
    throw error
  }
}
